import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";

const card = "rounded-2xl border border-adminborder bg-admincard";
const darkInput = "w-full rounded-lg border border-adminborder bg-adminbg px-3.5 py-2.5 text-sm text-slate-200 outline-none focus:border-saffron";

const ROLES = ["", "customer", "franchise", "agent", "branch_manager", "corporate_customer", "crm", "pickup", "ccare"];

export default function AdminCustomers() {
  const [users, setUsers] = useState([]);
  const [role, setRole] = useState("");
  const [q, setQ] = useState("");
  const [adjust, setAdjust] = useState(null);
  const [adjForm, setAdjForm] = useState({ amount: "", reason: "" });
  const [creating, setCreating] = useState(false);
  const [newUser, setNewUser] = useState({ name: "", email: "", password: "", role: "franchise", phone: "", territory: "", address: "", city: "", state: "", pincode: "" });
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({});

  const load = () => {
    const params = new URLSearchParams();
    if (role) params.set("role", role);
    if (q) params.set("q", q);
    api.get(`/admin/customers?${params}`).then((r) => setUsers(r.data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [role, q]);

  const saveAdjust = async () => {
    try {
      await api.post(`/admin/customers/${adjust.id}/wallet-adjust`, { amount: parseFloat(adjForm.amount), reason: adjForm.reason });
      toast.success("Wallet adjusted");
      setAdjust(null);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const saveUser = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post("/admin/users", newUser);
      toast.success(res.data.shipmozo_warehouse_id
        ? `Agent created · INDIAFAST warehouse #${res.data.shipmozo_warehouse_id}`
        : `${newUser.role} account created`);
      setCreating(false);
      setNewUser({ name: "", email: "", password: "", role: "franchise", phone: "", territory: "", address: "", city: "", state: "", pincode: "" });
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const setF = (k) => (e) => setEditForm({ ...editForm, [k]: e.target.value });

  const openUserEdit = (u) => {
    setEditingUser(u);
    setEditForm({ name: u.name || "", email: u.email || "", phone: u.phone || "", role: u.role,
                  company: u.company || "", territory: u.territory || "", address: u.address || "",
                  city: u.city || "", state: u.state || "", pincode: u.pincode || "",
                  kyc_status: u.kyc_status || "pending", password: "" });
  };

  const saveUserEdit = async () => {
    try {
      const payload = { ...editForm };
      if (!payload.password) delete payload.password;
      await api.put(`/admin/users/${editingUser.id}`, payload);
      toast.success("User updated");
      setEditingUser(null);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete ${u.name} (${u.email})? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const impersonate = async (u) => {
    try {
      const res = await api.post(`/admin/impersonate/${u.id}`);
      localStorage.setItem("if_impersonator", localStorage.getItem("if_token"));
      localStorage.setItem("if_token", res.data.access_token);
      toast.success(`Logged in as ${u.name}`);
      window.location.href = { crm: "/crm", pickup: "/pickup" }[u.role] || "/app";
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div data-testid="admin-customers" className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <h1 className="font-heading text-2xl font-bold tracking-tight text-white sm:text-3xl">Customers & Partners</h1>
        <button onClick={() => setCreating(true)} data-testid="create-user-btn"
                className="rounded-full bg-saffron px-5 py-2.5 text-sm font-bold text-white hover:bg-saffron-hover">+ Create Franchise / Agent</button>
      </div>

      <div className="flex flex-wrap gap-3">
        <input className={`${darkInput} max-w-xs`} data-testid="customers-search" placeholder="Search name / email / phone"
               value={q} onChange={(e) => setQ(e.target.value)} />
        <select className={`${darkInput} max-w-xs`} data-testid="customers-role-filter" value={role} onChange={(e) => setRole(e.target.value)}>
          {ROLES.map((r) => <option key={r} value={r}>{r ? r.replace("_", " ") : "All roles"}</option>)}
        </select>
      </div>

      <div className={`${card} overflow-x-auto`}>
        <table className="w-full text-sm" data-testid="customers-table">
          <thead>
            <tr className="border-b border-adminborder text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="px-5 py-3.5">Name</th><th className="px-5 py-3.5">Email</th><th className="px-5 py-3.5">Role</th>
              <th className="px-5 py-3.5">Wallet</th><th className="px-5 py-3.5">KYC</th><th className="px-5 py-3.5"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-adminborder/60 last:border-0 hover:bg-white/5">
                <td className="px-5 py-3.5 font-semibold text-white">{u.name}{u.is_demo ? <span className="ml-1 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-bold text-amber-400">DEMO</span> : null}</td>
                <td className="px-5 py-3.5 text-slate-400">{u.email}</td>
                <td className="px-5 py-3.5"><span className="rounded-full bg-white/10 px-2.5 py-1 text-xs font-bold capitalize">{u.role?.replace("_", " ")}</span></td>
                <td className="px-5 py-3.5 font-semibold">₹{(u.wallet_balance || 0).toLocaleString("en-IN")}</td>
                <td className="px-5 py-3.5 text-xs text-slate-400">{u.kyc_status || "—"}</td>
                <td className="px-5 py-3.5">
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => { setAdjust(u); setAdjForm({ amount: "", reason: "" }); }} data-testid={`wallet-adjust-${u.id}`}
                            className="rounded-lg border border-adminborder px-3 py-1.5 text-xs font-bold text-slate-300 hover:border-saffron hover:text-saffron">
                      Wallet ±
                    </button>
                    <button onClick={() => openUserEdit(u)} data-testid={`user-edit-${u.id}`}
                            className="rounded-lg border border-blue-500/40 px-3 py-1.5 text-xs font-bold text-blue-400 hover:bg-blue-500/10">
                      Edit
                    </button>
                    {!["admin", "super_admin"].includes(u.role) && (
                      <button onClick={() => impersonate(u)} data-testid={`user-impersonate-${u.id}`}
                              className="rounded-lg border border-emerald-500/40 px-3 py-1.5 text-xs font-bold text-emerald-400 hover:bg-emerald-500/10">
                        Login
                      </button>
                    )}
                    {!["admin", "super_admin"].includes(u.role) && (
                      <button onClick={() => deleteUser(u)} data-testid={`user-del-${u.id}`}
                              className="rounded-lg border border-red-500/40 px-3 py-1.5 text-xs font-bold text-red-400 hover:bg-red-500/10">
                        Delete
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {!users.length && <tr><td colSpan={6} className="px-5 py-12 text-center text-slate-500">No users found.</td></tr>}
          </tbody>
        </table>
      </div>

      {adjust && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" data-testid="wallet-adjust-modal">
          <div className={`${card} w-full max-w-md p-6`}>
            <h3 className="font-heading text-lg font-bold text-white">Adjust Wallet — {adjust.name}</h3>
            <p className="mt-1 text-xs text-slate-500">Current balance ₹{(adjust.wallet_balance || 0).toLocaleString("en-IN")}</p>
            <div className="mt-4 space-y-3">
              <input type="number" className={darkInput} data-testid="wallet-adjust-amount" placeholder="Amount (+ credit / - debit)"
                     value={adjForm.amount} onChange={(e) => setAdjForm({ ...adjForm, amount: e.target.value })} />
              <input className={darkInput} data-testid="wallet-adjust-reason" placeholder="Reason"
                     value={adjForm.reason} onChange={(e) => setAdjForm({ ...adjForm, reason: e.target.value })} />
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <button onClick={() => setAdjust(null)} className="rounded-lg border border-adminborder px-4 py-2 text-sm font-bold text-slate-300">Cancel</button>
              <button onClick={saveAdjust} data-testid="wallet-adjust-save-btn" className="rounded-lg bg-saffron px-4 py-2 text-sm font-bold text-white">Apply</button>
            </div>
          </div>
        </div>
      )}

      {editingUser && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 p-4" data-testid="edit-user-modal">
          <div className={`${card} mx-auto my-8 w-full max-w-lg p-6`}>
            <h3 className="font-heading text-lg font-bold text-white">Edit User — <span className="text-saffron">{editingUser.name}</span></h3>
            <p className="mt-1 text-xs text-slate-500">Leave password blank to keep the current one.</p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <input className={darkInput} data-testid="edit-user-name" placeholder="Full name" value={editForm.name} onChange={setF("name")} />
              <input type="email" className={darkInput} data-testid="edit-user-email" placeholder="Email" value={editForm.email} onChange={setF("email")} />
              <input className={darkInput} data-testid="edit-user-phone" placeholder="Phone" value={editForm.phone} onChange={setF("phone")} />
              <select className={darkInput} data-testid="edit-user-role" value={editForm.role} onChange={setF("role")}>
                {["customer", "corporate_customer", "franchise", "agent", "branch_manager", "crm", "pickup", "delivery_executive", "admin"].map((r) => <option key={r} value={r}>{r.replace("_", " ")}</option>)}
              </select>
              <input className={darkInput} data-testid="edit-user-company" placeholder="Company" value={editForm.company} onChange={setF("company")} />
              <input className={darkInput} data-testid="edit-user-territory" placeholder="Territory" value={editForm.territory} onChange={setF("territory")} />
              <input className={`${darkInput} sm:col-span-2`} data-testid="edit-user-address" placeholder="Address" value={editForm.address} onChange={setF("address")} />
              <input className={darkInput} data-testid="edit-user-city" placeholder="City" value={editForm.city} onChange={setF("city")} />
              <input className={darkInput} data-testid="edit-user-state" placeholder="State" value={editForm.state} onChange={setF("state")} />
              <input className={darkInput} data-testid="edit-user-pincode" placeholder="Pincode" value={editForm.pincode} onChange={setF("pincode")} />
              <select className={darkInput} data-testid="edit-user-kyc" value={editForm.kyc_status} onChange={setF("kyc_status")}>
                {["pending", "verified", "rejected"].map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
              <input type="password" className={`${darkInput} sm:col-span-2`} data-testid="edit-user-password" placeholder="New password (optional, min 8 chars)" value={editForm.password} onChange={setF("password")} />
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <button onClick={() => setEditingUser(null)} className="rounded-lg border border-adminborder px-4 py-2 text-sm font-bold text-slate-300">Cancel</button>
              <button onClick={saveUserEdit} data-testid="edit-user-save-btn" className="rounded-lg bg-saffron px-5 py-2 text-sm font-bold text-white hover:bg-saffron-hover">Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {creating && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" data-testid="create-user-modal">
          <form onSubmit={saveUser} className={`${card} w-full max-w-md p-6`}>
            <h3 className="font-heading text-lg font-bold text-white">Create Partner Account</h3>
            <div className="mt-4 space-y-3">
              <select className={darkInput} data-testid="new-user-role" value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}>
                <option value="franchise">Franchise</option><option value="agent">Agent</option>
                <option value="branch_manager">Branch Manager</option><option value="customer">Customer</option>
                <option value="admin">Admin</option><option value="delivery_executive">Delivery Executive</option>
                <option value="crm">CRM Desk</option><option value="pickup">Pickup Boy</option>
                <option value="ccare">CCare (Customer Care — tracking only)</option>
              </select>
              <input required className={darkInput} data-testid="new-user-name" placeholder="Full name" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} />
              <input required type="email" className={darkInput} data-testid="new-user-email" placeholder="Email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} />
              <input required minLength={8} className={darkInput} data-testid="new-user-password" placeholder="Password (min 8 chars)" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} />
              <input required={newUser.role === "agent" || newUser.role === "pickup"} className={darkInput} data-testid="new-user-phone" placeholder="Phone (10-digit, required for agents/pickup boys)" value={newUser.phone} onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })} />
              <input className={darkInput} data-testid="new-user-territory" placeholder="Territory / City (franchise)" value={newUser.territory} onChange={(e) => setNewUser({ ...newUser, territory: e.target.value })} />
              {(newUser.role === "agent" || newUser.role === "pickup") && (
                <div className="space-y-3 rounded-xl border border-saffron/40 bg-saffron/10 p-3.5">
                  <p className="text-xs font-bold text-saffron">Agent / Pickup Boy = INDIAFAST Warehouse — exact pickup address required</p>
                  <input required className={darkInput} data-testid="new-user-address" placeholder="Exact address (line 1) e.g. Shop 12, MG Road"
                         value={newUser.address} onChange={(e) => setNewUser({ ...newUser, address: e.target.value })} />
                  <div className="grid grid-cols-3 gap-2">
                    <input required className={darkInput} data-testid="new-user-city" placeholder="City"
                           value={newUser.city} onChange={(e) => setNewUser({ ...newUser, city: e.target.value })} />
                    <input required className={darkInput} data-testid="new-user-state" placeholder="State"
                           value={newUser.state} onChange={(e) => setNewUser({ ...newUser, state: e.target.value })} />
                    <input required maxLength={6} className={darkInput} data-testid="new-user-pincode" placeholder="Pincode"
                           value={newUser.pincode} onChange={(e) => setNewUser({ ...newUser, pincode: e.target.value })} />
                  </div>
                </div>
              )}
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <button type="button" onClick={() => setCreating(false)} className="rounded-lg border border-adminborder px-4 py-2 text-sm font-bold text-slate-300">Cancel</button>
              <button type="submit" data-testid="create-user-save-btn" className="rounded-lg bg-saffron px-4 py-2 text-sm font-bold text-white">Create</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
